// src/modules/app-views/products/pages/Init/modals/ProductsParametersModal/tabs/TagsTab.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useMemo } from 'react';
import { Table, Button, Flex, Space, Popconfirm, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import appService from 'services/appService';
import { generateUidV4 } from 'utils/helpers';
import useZustand from 'hooks/useZustand';
import TagFormModal from './TagFormModal';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const TagsTab = ({ loading, setLoading, moduleId: moduleIdProp }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedModule = useZustand('module', 'config');
  const moduleId = moduleIdProp || storedModule?.id || 'products';

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [mousePosition, setMousePosition] = useState(null);

  // Use local state for tags — initialize from store, update locally after save
  const [tags, setTags] = useState(() => {
    const rawTags = appService.getModulePreferences({ moduleId, category: 'tags' });
    return normalizeTags(rawTags);
  });

  // Filter out removed tags for display
  const visibleTags = useMemo(() => tags.filter(t => !t.removed), [tags]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleAdd = useCallback(e => {
    setMousePosition({ x: e.clientX, y: e.clientY });
    setEditingRecord(null);
    setShowModal(true);
  }, []);

  const handleEdit = useCallback((record, e) => {
    setMousePosition({ x: e.clientX, y: e.clientY });
    setEditingRecord(record);
    setShowModal(true);
  }, []);

  const handleSave = useCallback(
    async formData => {
      // Check for duplicates (excluding current item if editing, and excluding removed)
      const isDuplicate = tags.some(
        t =>
          t.uid !== editingRecord?.uid &&
          !t.removed &&
          t.label.toLowerCase() === formData.label.toLowerCase()
      );

      if (isDuplicate) {
        appService.message({ flag: 'w', text: 'item_already_exists' });
        return;
      }

      setLoading(true);
      try {
        let updatedTags;

        if (editingRecord) {
          updatedTags = tags.map(t =>
            t.uid === editingRecord.uid
              ? { ...t, label: formData.label, color: formData.color }
              : t
          );
        } else {
          updatedTags = [
            ...tags,
            {
              uid: generateUidV4(),
              label: formData.label,
              color: formData.color,
            },
          ];
        }

        await appService.updateModulePreferences({
          moduleId,
          category: 'tags',
          data: updatedTags,
        });

        setTags(updatedTags);
        appService.message({ flag: 's', text: 'saved_successfully' });
        setShowModal(false);
        setEditingRecord(null);
      } catch (error) {
        console.error('[TagsTab] Save error:', error);
        appService.message({ flag: 'e', text: 'error_saving' });
      } finally {
        setLoading(false);
      }
    },
    [tags, editingRecord, setLoading, moduleId]
  );

  const handleCancel = useCallback(() => {
    setShowModal(false);
    setEditingRecord(null);
  }, []);

  const handleDelete = useCallback(
    async record => {
      setLoading(true);
      try {
        const isInUse = await appService.checkParameterInUse({
          module: moduleId,
          category: 'tags',
          uid: record.uid,
        });

        let updatedTags;

        if (isInUse) {
          updatedTags = tags.map(t =>
            t.uid === record.uid ? { ...t, removed: true } : t
          );
          appService.message({ flag: 'w', text: 'item_in_use_hidden' });
        } else {
          updatedTags = tags.filter(t => t.uid !== record.uid);
          appService.message({ flag: 's', text: 'removed_successfully' });
        }

        await appService.updateModulePreferences({
          moduleId,
          category: 'tags',
          data: updatedTags,
        });

        setTags(updatedTags);
      } catch (error) {
        console.error('[TagsTab] Delete error:', error);
        appService.message({ flag: 'e', text: 'error_removing' });
      } finally {
        setLoading(false);
      }
    },
    [tags, setLoading, moduleId]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. TABLE COLUMNS
  // ─────────────────────────────────────────────────────────────────────────────
  const columns = [
    {
      title: translateX('tag'),
      dataIndex: 'label',
      render: (text, record) => (
        <Tag color={record.color} style={{ margin: 0 }}>
          {text}
        </Tag>
      ),
    },
    {
      title: translateX('actions'),
      dataIndex: 'actions',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={e => handleEdit(record, e)}
            disabled={loading}
          />
          <Popconfirm
            title={translateX('confirm_delete')}
            onConfirm={() => handleDelete(record)}
            okText={translateX('yes')}
            cancelText={translateX('no')}
          >
            <Button type="text" size="small" danger icon={<DeleteOutlined />} disabled={loading} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Flex vertical style={{ height: '100%' }}>
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Table
          rowKey="uid"
          columns={columns}
          dataSource={visibleTags}
          pagination={false}
          size="small"
          loading={loading}
          locale={{ emptyText: translateX('no_data') }}
          scroll={{ y: 'calc(80vh - 240px)' }}
        />
      </div>

      <Flex justify="flex-end" className="mt-3" style={{ flexShrink: 0 }}>
        <Button icon={<PlusOutlined />} onClick={handleAdd} disabled={loading}>
          {translateX('add_tag')}
        </Button>
      </Flex>

      <TagFormModal
        open={showModal}
        onCancel={handleCancel}
        onSave={handleSave}
        initialData={editingRecord}
        loading={loading}
        mousePosition={mousePosition}
      />
    </Flex>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default TagsTab;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function normalizeTags(rawData) {
  if (!rawData || !Array.isArray(rawData)) return [];
  return rawData.map(normalizeTag);
}

function normalizeTag(tag) {
  return {
    uid: tag.uid,
    label: tag.label || '',
    color: tag.color || '#1677ff',
    ...(tag.removed && { removed: true }),
  };
}
